import React from 'react';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client'; // Importing createRoot
import './index.css';
import { Provider } from 'react-redux';
import { store } from './store/store';
import MainApp from './MainApp';

// Sentry initialization — must be before createRoot()
// Exceptions + breadcrumbs — no performance traces, no session replays.
// Breadcrumbs give "what happened before the crash" context for free.
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_APP_ENV || process.env.REACT_APP_ENV || 'localhost',
    initialScope: {
      tags: { service: 'client' },
    },
    maxBreadcrumbs: 50,
    ignoreErrors: [
      'AbortError',
      'Non-Error promise rejection captured',
      /Failed to fetch/i,
      /NetworkError/i,
      /Load failed/i,
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],
    denyUrls: [
      /extensions\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
    ],
    beforeSend(event, hint) {
      const err: any = hint?.originalException;
      if (err?.name === 'AbortError') return null;
      if (err?.message && /failed to fetch|networkerror|load failed/i.test(err.message)) return null;
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (
        breadcrumb.category === 'console' &&
        typeof breadcrumb.message === 'string' &&
        breadcrumb.message.startsWith('[callAPI]')
      ) {
        return null;
      }
      return breadcrumb;
    },
  });
}

// Create a root
const container = document.getElementById('root')!;
const root = createRoot(container);

// Render the app within the root
root.render(
  <React.Fragment>
    <Provider store={store}>
      <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please refresh the page.</p>}>
        <MainApp />
      </Sentry.ErrorBoundary>
    </Provider>
  </React.Fragment>
);
